/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { of } from 'rxjs';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { PersonUnitRole } from '@src/app/model/person-unit-role';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { Action } from '@src/app/model/action';
import { Memo } from '@src/app/model/memo';
import { MailItem, MailMerge } from '@src/app/model/mailMerge';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { Attachment } from '@src/app/model/attachment';
import { User } from '@src/app/model/user';

export type MailStatusType =
  | 'OK'
  | 'OptOut'
  | 'Bounce'
  | 'Complaint'
  | 'Invalid';

export class MAIL_STATUS {
    static readonly ok: MailStatusType = 'OK';
    static readonly optOut: MailStatusType = 'OptOut';
    static readonly bounce: MailStatusType = 'Bounce';
    static readonly complaint: MailStatusType = 'Complaint';
    static readonly invalid: MailStatusType = 'Invalid';

    static items: AbstractObject[] = [
        { id: MAIL_STATUS.ok, name: 'OK' },
        { id: MAIL_STATUS.optOut, name: 'User opted out' },
        { id: MAIL_STATUS.bounce, name: 'Email bounced' },
        { id: MAIL_STATUS.complaint, name: 'User marked as spam/complaint' },
        { id: MAIL_STATUS.invalid, name: 'Invalid email address' },
    ];

    static getDescription(status: MailStatusType) {
        const s = MAIL_STATUS.items.find(s => s.id === status);
        if (s) {
            return s.name;
        }
        return 'Unknown';
    }
}

export type EntityType =
  | 'ind'
  | 'org'
  | 'choose'

export class EntityTypes {
    static readonly individual : EntityType = 'ind';
    static readonly organisation : EntityType = 'org';
    static readonly choose : EntityType = 'choose';

    static items: AbstractObject[] = [
        { id: EntityTypes.individual, name: $localize `Individual`},
        { id: EntityTypes.organisation, name: $localize `Organisation`},
    ];
    static chooseType: AbstractObject[] = [
        { id: EntityTypes.individual, name: $localize `Add Individual`},
        { id: EntityTypes.organisation, name: $localize `Add Organisation`},
        { id: EntityTypes.choose, name: $localize `Choose Existing Contact`}
    ]

    static readonly personType 

    static getDescription(type: EntityType) {
        const s = ContactTypes.items.find(s => s.id === type);
        if (s) {
            return s.name;
        }
        return 'Unknown';
    }
}

export type ContactType =
  | 'participant'
  | 'director'
  | 'past'
  | 'accountant'
  | 'interested'
  | 'legal'
  | 'inspector'
  | 'supplier'
  | 'unknown'
  | 'agent'
export interface ContactTypeItem extends AbstractObject {
    agentSelectable: boolean;
    omcSelectable: boolean;
}

export class ContactTypes {
    static readonly participant : ContactType = 'participant';
    static readonly accountant : ContactType = 'accountant';
    static readonly interested : ContactType = 'interested';
    static readonly legal : ContactType = 'legal';
    static readonly inspector : ContactType = 'inspector';
    static readonly supplier : ContactType = 'supplier';
    static readonly unknown : ContactType = 'unknown';
    static readonly director : ContactType = 'director';
    static readonly past : ContactType = 'past';
    static readonly agent : ContactType = 'agent';

    // notes are written as view descriptions — the omcSelectable ones become the helpText of the
    // contact-type views generated in person-page-nav.ts
    static items: ContactTypeItem[] = [
        { id: ContactTypes.participant, name: $localize `OMC Participant`, omcSelectable: true, agentSelectable: false,
            notes: $localize `Current and future owners and residents of the OMC`},
        { id: ContactTypes.past, name: $localize `Past Participant`, omcSelectable: true, agentSelectable: true,
            notes: $localize `Contacts previously involved with the OMC, but no longer active`},
        { id: ContactTypes.director, name: $localize `Director`, omcSelectable: true, agentSelectable: false,
            notes: $localize `Contacts who serve as directors of the OMC`},
        { id: ContactTypes.accountant, name: $localize `Accountant`, omcSelectable: true, agentSelectable: false,
            notes: $localize `Accounting professionals working with the OMC`},
        { id: ContactTypes.interested, name: $localize `Interested party`, omcSelectable: true, agentSelectable: false,
            notes: $localize `Neighbours and other people with a non professional interest in the business of the OMC`},
        { id: ContactTypes.legal, name: $localize `Solicitor`, omcSelectable: true, agentSelectable: false,
            notes: $localize `Solicitors and other legal professionals engaged in a professional capacity with the OMC`},
        { id: ContactTypes.inspector, name: $localize `Regulatory Professional`, omcSelectable: true, agentSelectable: false,
            notes: $localize `Inspectors from Council, Fire Brigade, Loss Adjusters, Risk Assessors etc.`},
        { id: ContactTypes.supplier, name: $localize `Supplier`, omcSelectable: true, agentSelectable: true,
            notes: $localize `People and companies who supply goods or services to the OMC`},
        { id: ContactTypes.agent, name: $localize `Agency Staff`, omcSelectable: true, agentSelectable: true,
            notes: $localize `Staff and owners of the managing agency`},
        { id: ContactTypes.unknown, name: $localize `Unknown`, omcSelectable: true, agentSelectable: false,
            notes: $localize `Contacts not yet classified, e.g. created automatically from an unknown email address`},
    ];

    // The contact types an agency user may assign on the agent contacts page — agency staff,
    // their suppliers' contacts, past contacts, and the AI assistant. (Never OMC-participant types.)
    static agentSelectables(): ContactTypeItem[] {
        return ContactTypes.items.filter(ct => ct.agentSelectable);
    }

    // The contact types an OMC user may assign, and the views on the OMC contacts page. The AI
    // assistant is an agency record that OMC users never file under, so it is not one of them.
    static omcSelectables(): ContactTypeItem[] {
        return ContactTypes.items.filter(ct => ct.omcSelectable);
    }

    static readonly personType 

    static getDescription(type: ContactType) {
        const s = ContactTypes.items.find(s => s.id === type);
        if (s) {
            return s.name;
        }
        return 'Unknown';
    }
}

export class Person extends AbstractObject {

    contactType?: ContactType = ContactTypes.participant;
    entityType?: EntityType = EntityTypes.individual;
    title: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    address?: string;
    userId?: uuid;
    fullName?: string;
    mailStatus?: MailStatusType;
    primaryContactId?: uuid;
    primaryContactName?:string;
    supplierId?: uuid;
    integrationId?:string;
    //units: PersonUnitRole[] = [];
    associatedContacts?: Person[] = [];
    personUnits: PersonUnitRole[] = [];
    units?: PersonUnitRole[] = []; // calc from personUnits
    roles?: PersonUnitRole[] = []; // calc from personUnits
    actions?: Action[] = [];
    memos?: Memo[] = [];
    mailItems?: MailItem[] = [];
    mailMerges?: MailMerge[] = [];
    attachments?: Attachment[] = [];
    // Not Really on person table...
    invoices?: number;
    outstanding?: number;
    unitName?: string;
    // Only for insert PersonUnitRole at same time
    unit?: {unitId: uuid, resident: boolean, owner: boolean}
    unitId?: uuid;
    roleId?: uuid;
    user?: User;

    constructor(o: Partial<Person> = {}) {
        super(o);
    }

    static validEmail = (email: unknown): boolean => {
        if (typeof email !== 'string') return false;
        if (!email.trim()) return false; // handles empty string or whitespace
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    static titles: AbstractObject[] = [
        { id: '', name: '' },
        { id: 'Mr', name: 'Mr' },
        { id: 'Ms', name: 'Ms' },
        { id: 'Mrs', name: 'Mrs' },
        { id: 'Miss', name: 'Miss' },
        { id: 'Dr', name: 'Dr' },
        { id: 'Prof', name: 'Prof' },
        { id: 'Rt Hon.', name: 'Rt Hon.' },
    ]
    static getTitleField() {
        return FormPicklistComponent.make('Title', 'title', null, { items: Person.titles });
    }
    static pickDialogFormConfig(): FormConfig {
        return new FormConfig({
            fieldSet: new FieldSet({
                fields: [
                    FieldMaker.id(),
                    FieldMaker.rev(),
                    Person.getTitleField(),
                    FormTextComponent.make('First Name', 'firstName'),
                    FormTextComponent.make('Last Name', 'lastName'),
                    FormTextComponent.make('Telephone', 'phone', {formColumn: 2}),
                    FormTextComponent.make('Email', 'email', {formColumn: 2}),
                    FormTextAreaComponent.make('Address', 'address', {formColumn: 3}),
                    FieldMaker.notes().override({formColumn: 3}),
                ],
                formLayout: LAYOUT_OPTIONS.threeCol,
            }),
            title: $localize`People`,
            mode: 'new',
            objectFactory: () => of(new Person()),
        });
    }

}
